import { useCallback, useEffect, useMemo, useRef } from 'react';
import { css } from '@linaria/atomic';
import { useQueryClient } from 'react-query';
import isEmail from 'validator/es/lib/isEmail';
import { CareCreditIntegrationCustom, CareCreditIntegrationV1 } from '@frontend/api-care-credit-integration';
import { useValidPhoneNumberFromPerson } from '../../../hooks/use-valid-phone-number-from-person';
import { stateOptions } from '@frontend/geography';
import { useTranslation } from '@frontend/i18n';
import {
  Button,
  ContentLoader,
  DropdownField,
  EmailField,
  Heading,
  PhoneField,
  Text,
  TextField,
  useAlert,
  useForm,
  useTooltip,
} from '@frontend/design-system';
import { useCollectPaymentMultiContext } from '../../collect-payment-multi.context';
import { PAYMENT_MULTI_STEP_LABELS, PAYMENT_MULTI_STEPS } from '../../utils/steps';
import { StepPerformanceWrapper } from '../atoms/step-performance-wrapper';
import { StyledStep } from '../atoms/styled-step';

const styles = {
  address: css`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 120px 120px;
    gap: var(--fabric-space-gap-md);
  `,
  heading: css`
    margin-top: var(--fabric-space-gap-lg);
    margin-bottom: var(--fabric-space-gap-md);
  `,
  name: css`
    display: flex;
    flex-direction: column;
    margin-bottom: var(--fabric-space-gap-md);
  `,
  readonlyInput: css`
    display: flex;
    flex-direction: column;
    min-height: 40px;
    justify-content: center;
  `,
  row: css`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: var(--fabric-space-gap-md);
  `,
  section: css`
    padding-bottom: var(--fabric-space-padding-xxs);
  `,
  step: css`
    .step__body {
      margin-bottom: 0 !important;
    }
  `,
  street: css`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: var(--fabric-space-gap-md);
    margin-bottom: var(--fabric-space-gap-md);
    margin-top: var(--fabric-space-gap-sm);
  `,
};

type VerificationFormValues = {
  mobileNumber: string;
  email: string;
  addressLine1: string;
  city: string;
  state: string;
  zipCode: string;
};

export const CareCreditVerifyStatus = () => (
  <StepPerformanceWrapper step={PAYMENT_MULTI_STEPS.careCreditVerifyStatus}>
    <CareCreditVerifyStatusBody />
  </StepPerformanceWrapper>
);

const CareCreditVerifyStatusBody = () => {
  const { t } = useTranslation('payments');
  const alerts = useAlert();
  const queryClient = useQueryClient();
  const {
    activeLocationId,
    careCreditData,
    closeOnCareCreditVerification,
    hideBackButtonOnCareCreditVerification,
    person,
    multiStep: { goPrevStep, onCancel: onCancelModal, onComplete },
  } = useCollectPaymentMultiContext();
  const { firstValidPhoneNumber } = useValidPhoneNumberFromPerson(activeLocationId, person);

  const { data: careCreditPersonResponse } = CareCreditIntegrationV1.Queries.useGetCareCreditPersonQuery({
    request: {
      locationId: activeLocationId ?? '',
      weavePersonId: person?.personId ?? '',
    },
    options: {
      enabled: !!activeLocationId && !!person?.personId,
      retry: false,
    },
  });

  const { mutateAsync: verifyCareCreditPerson, isLoading: isVerifyingCareCreditPerson } =
    CareCreditIntegrationV1.Mutations.useVerifyCareCreditPersonMutation({
      options: {
        retry: false,
      },
    });
  const fallbackFields = useMemo(() => CareCreditIntegrationCustom.getPatientQueryFields(person), [person]);
  const initialValues = useMemo<VerificationFormValues>(() => {
    const careCreditPerson = careCreditPersonResponse?.person;

    return {
      mobileNumber: careCreditPerson?.mobileNumber || firstValidPhoneNumber || fallbackFields?.contactNumber || '',
      email: careCreditPerson?.email || fallbackFields?.email || '',
      addressLine1: careCreditPerson?.address?.addressLine1 || fallbackFields?.addressLine1 || '',
      city: careCreditPerson?.address?.city || fallbackFields?.city || '',
      state: careCreditPerson?.address?.state || fallbackFields?.state || '',
      zipCode: careCreditPerson?.address?.zipCode || fallbackFields?.zipCode || '',
    };
  }, [careCreditPersonResponse?.person, fallbackFields]);
  const seededValuesRef = useRef(initialValues);
  const firstName = careCreditPersonResponse?.person?.firstName || fallbackFields?.firstName || '';
  const lastName = careCreditPersonResponse?.person?.lastName || fallbackFields?.lastName || '';

  const { getFieldProps, isComplete, seedValues, validate, values } = useForm({
    fields: {
      mobileNumber: {
        type: 'phone',
        required: false,
        value: initialValues.mobileNumber,
        validateOnChange: true,
        validator: (field) => {
          const digits = field.value.replace(/\D/g, '');
          return !digits || digits.length === 10 ? '' : t('Invalid phone number');
        },
      },
      email: {
        type: 'email',
        required: false,
        value: initialValues.email,
        validator: (field) => {
          if (!field.value.trim()) return '';
          if (field.value.length > 50) return t('Email cannot exceed 50 characters');
          return isEmail(field.value) ? '' : t('Invalid email address');
        },
      },
      addressLine1: {
        type: 'text',
        required: true,
        value: initialValues.addressLine1,
      },
      city: {
        type: 'text',
        required: true,
        value: initialValues.city,
      },
      state: {
        type: 'dropdown',
        required: true,
        value: initialValues.state,
      },
      zipCode: {
        type: 'text',
        required: true,
        value: initialValues.zipCode,
      },
    },
  });

  useEffect(() => {
    const fieldsWereEdited = (Object.keys(seededValuesRef.current) as Array<keyof VerificationFormValues>).some(
      (field) => values[field] !== seededValuesRef.current[field]
    );

    if (!fieldsWereEdited) {
      seedValues(initialValues);
      seededValuesRef.current = initialValues;
    }
  }, [initialValues, seedValues, values]);

  const onGoBack = useCallback(() => {
    const previousStep =
      careCreditData?.status === 'PREQUALIFICATION_NEEDED'
        ? PAYMENT_MULTI_STEPS.careCreditEnrollDisclosure
        : PAYMENT_MULTI_STEPS.paymentMethods;

    goPrevStep({ orStep: previousStep });
  }, [careCreditData?.status, goPrevStep]);

  const onVerify = useCallback(async () => {
    if (!isComplete || !activeLocationId || !person?.personId) {
      validate();
      return;
    }

    try {
      const mobileNumber = values.mobileNumber || '';
      const email = values.email || '';
      const addressLine1 = values.addressLine1 || '';
      const city = values.city || '';
      const zipCode = values.zipCode || '';

      const verificationRequest = {
        locationId: activeLocationId,
        weavePersonId: person.personId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        ...(mobileNumber && { mobileNumber: mobileNumber.replace(/\D/g, '') }),
        ...(email.trim() && { email: email.trim() }),
        address: {
          addressLine1: addressLine1.trim(),
          city: city.trim(),
          state: values.state || '',
          zipCode: zipCode.trim(),
        },
      };
      const response = await verifyCareCreditPerson(verificationRequest);

      if (!response.weaveStatus) {
        throw new Error(t('An error occurred while verifying CareCredit status. Please try again.'));
      }

      if (closeOnCareCreditVerification) {
        onComplete();
      } else {
        goPrevStep({ orStep: PAYMENT_MULTI_STEPS.paymentMethods });
      }
      alerts.success(t('CareCredit account status verified successfully'));
    } catch {
      alerts.error(t('An error occurred while verifying CareCredit status. Please try again.'));
    }
  }, [
    activeLocationId,
    alerts,
    closeOnCareCreditVerification,
    firstName,
    lastName,
    goPrevStep,
    isComplete,
    onComplete,
    person?.personId,
    queryClient,
    t,
    validate,
    values,
    verifyCareCreditPerson,
  ]);

  const { Tooltip, tooltipProps, triggerProps } = useTooltip();
  const isVerifyStatusDisabled = !isComplete || isVerifyingCareCreditPerson || !activeLocationId || !person?.personId;
  const verifyStatusTooltip = t('All fields must be filled to verify CareCredit Status');

  return (
    <StyledStep
      className={styles.step}
      isComplete={false}
      id={PAYMENT_MULTI_STEPS.careCreditVerifyStatus}
      label={PAYMENT_MULTI_STEP_LABELS[PAYMENT_MULTI_STEPS.careCreditVerifyStatus]}
      hasInternalFooter
      footer={{
        primary: (buttonProps) => (
          <>
            <Button
              {...buttonProps}
              {...(!isComplete ? triggerProps : {})}
              disabled={isVerifyStatusDisabled}
              loading={isVerifyingCareCreditPerson}
              size='large'
            >
              {t('Verify Status')}
            </Button>
            {!isComplete && <Tooltip {...tooltipProps}>{verifyStatusTooltip}</Tooltip>}
          </>
        ),
        secondary: {
          children: t('Cancel'),
          disabled: isVerifyingCareCreditPerson,
        },
        onClickBack: onGoBack,
        onClickPrimary: onVerify,
        onClickSecondary: onCancelModal,
        showBackButton: !hideBackButtonOnCareCreditVerification,
      }}
    >
      <section className={styles.section}>
        <ContentLoader show={isVerifyingCareCreditPerson} />
        <Text>{t("Complete the missing patient information below to verify the patient's CareCredit status.")}</Text>

        <Heading level={3} className={styles.heading}>
          {t('Personal')}
        </Heading>
        <div className={styles.row}>
          <div className={styles.name}>
            <Text size='small' color='subdued'>
              {t('First Name')}
            </Text>
            <div className={styles.readonlyInput}>
              <Text size='large'>{firstName}</Text>
            </div>
          </div>
          <div className={styles.name}>
            <Text size='small' color='subdued'>
              {t('Last Name')}
            </Text>
            <div className={styles.readonlyInput}>
              <Text size='large'>{lastName}</Text>
            </div>
          </div>
        </div>
        <div className={styles.row}>
          <PhoneField {...getFieldProps('mobileNumber')} label={t('Mobile Number')} />
          <EmailField {...getFieldProps('email')} label={t('Email')} />
        </div>

        <Heading level={3} className={styles.heading}>
          {t('Address')}
        </Heading>
        <div className={styles.street}>
          <TextField {...getFieldProps('addressLine1')} label={t('Street Address')} />
        </div>
        <div className={styles.address}>
          <TextField {...getFieldProps('city')} label={t('City')} />
          <DropdownField {...getFieldProps('state')} label={t('State')}>
            {stateOptions.US.map((option) => (
              <DropdownField.Option key={option.value} value={option.value}>
                {option.value}
              </DropdownField.Option>
            ))}
          </DropdownField>
          <TextField {...getFieldProps('zipCode')} label={t('Zip Code')} />
        </div>
      </section>
    </StyledStep>
  );
};
